// src/features/fiscalization/NewReceiptModal.tsx
// Issues a fiscal receipt by hand — most receipts are auto-issued on payment
// events (per fiscal policy), this is the manual/ops path.
// Covers: CreateFiscalReceipt.

import { useEffect, useState } from 'react';
import { useI18n } from '@react/hooks';
import { useFiscalizationFiscalPoint } from '@react/hooks';
import { Button, Input } from '@react/components';
import { Select } from '@/components/Select';
import type { FiscalReceiptType, VatType, FiscalPaymentType } from '@core/types/fiscalization';
import { FiscalReceiptTypeValues, VatTypeValues, FiscalPaymentTypeValues } from '@core/types/fiscalization';
import { errMessage, humanizeEnum } from './format';

export interface NewReceiptValue {
  fiscal_point_id: string;
  type: FiscalReceiptType;
  customer_email: string;
  customer_phone: string;
  cashier_name: string;
  cashier_inn: string;
  item_name: string;
  item_quantity: string;
  item_unit_price: string;
  item_vat_type: VatType;
  payment_type: FiscalPaymentType;
}

const RECEIPT_TYPES = FiscalReceiptTypeValues.filter((v) => v !== 'FISCAL_RECEIPT_TYPE_UNSPECIFIED' && !v.includes('CORRECTION'));
const VAT_TYPES = VatTypeValues.filter((v) => v !== 'VAT_TYPE_UNSPECIFIED');
const PAYMENT_TYPES = FiscalPaymentTypeValues.filter((v) => v !== 'FISCAL_PAYMENT_TYPE_UNSPECIFIED');

export interface NewReceiptModalProps {
  open: boolean;
  isSaving?: boolean;
  onCancel: () => void;
  onSubmit: (value: NewReceiptValue) => Promise<void>;
}

export function NewReceiptModal({ open, isSaving, onCancel, onSubmit }: NewReceiptModalProps) {
  const { t } = useI18n();
  const pointStore = useFiscalizationFiscalPoint();
  const [form, setForm] = useState<NewReceiptValue>({
    fiscal_point_id: '', type: RECEIPT_TYPES[0], customer_email: '', customer_phone: '',
    cashier_name: '', cashier_inn: '', item_name: '', item_quantity: '1', item_unit_price: '',
    item_vat_type: VAT_TYPES[0], payment_type: PAYMENT_TYPES[0],
  });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && pointStore.items.length === 0) {
      pointStore.ListFiscalPoints({ pagination: { cursor: '', limit: 100, direction: 'forward' }, sort: [], date_range: {} as any, filters: {} as any } as any);
    }
  }, [open]);

  if (!open) return null;

  const change = <K extends keyof NewReceiptValue>(field: K, value: NewReceiptValue[K]) => setForm((p) => ({ ...p, [field]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await onSubmit(form);
    } catch (err) {
      setError(errMessage(err, t('fiscalization.receipts.createError')));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto py-8">
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={onCancel} aria-hidden="true" />
      <form onSubmit={handleSubmit} role="dialog" aria-modal="true" className="relative mx-4 w-full max-w-lg rounded-lg border border-border bg-background p-5 shadow-xl space-y-4">
        <h3 className="text-base font-semibold text-foreground">{t('fiscalization.receipts.new')}</h3>
        {error && <p className="text-sm text-destructive">{error}</p>}

        <Select label={t('fiscalization.receipts.fiscalPoint')} value={form.fiscal_point_id} onChange={(e) => change('fiscal_point_id', e.target.value)} required>
          <option value="">{t('common.select')}</option>
          {pointStore.items.map((fp) => (
            <option key={fp.entity?.id} value={fp.entity?.id}>{fp.name}</option>
          ))}
        </Select>

        <Select label={t('fiscalization.receipts.type')} value={form.type} onChange={(e) => change('type', e.target.value as FiscalReceiptType)}>
          {RECEIPT_TYPES.map((v) => (
            <option key={v} value={v}>{humanizeEnum(v, 'FISCAL_RECEIPT_TYPE_')}</option>
          ))}
        </Select>

        <div className="grid grid-cols-2 gap-3">
          <Input label={t('fiscalization.receipts.customerEmail')} type="email" value={form.customer_email} onChange={(e) => change('customer_email', e.target.value)} />
          <Input label={t('fiscalization.receipts.customerPhone')} value={form.customer_phone} onChange={(e) => change('customer_phone', e.target.value)} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input label={t('fiscalization.receipts.cashierName')} value={form.cashier_name} onChange={(e) => change('cashier_name', e.target.value)} required />
          <Input label={t('fiscalization.receipts.cashierInn')} value={form.cashier_inn} onChange={(e) => change('cashier_inn', e.target.value)} />
        </div>

        <div className="border-t border-border pt-3 space-y-3">
          <h4 className="text-sm font-medium text-foreground">{t('fiscalization.receipts.lineItem')}</h4>
          <Input label={t('fiscalization.receipts.itemName')} value={form.item_name} onChange={(e) => change('item_name', e.target.value)} required />
          <div className="grid grid-cols-3 gap-3">
            <Input label={t('fiscalization.receipts.quantity')} value={form.item_quantity} onChange={(e) => change('item_quantity', e.target.value)} required />
            <Input label={t('fiscalization.receipts.unitPrice')} type="number" step="0.01" value={form.item_unit_price} onChange={(e) => change('item_unit_price', e.target.value)} required />
            <Select label={t('fiscalization.receipts.vatType')} value={form.item_vat_type} onChange={(e) => change('item_vat_type', e.target.value as VatType)}>
              {VAT_TYPES.map((v) => (
                <option key={v} value={v}>{humanizeEnum(v, 'VAT_TYPE_')}</option>
              ))}
            </Select>
          </div>
        </div>

        <Select label={t('fiscalization.receipts.paymentType')} value={form.payment_type} onChange={(e) => change('payment_type', e.target.value as FiscalPaymentType)}>
          {PAYMENT_TYPES.map((v) => (
            <option key={v} value={v}>{humanizeEnum(v, 'FISCAL_PAYMENT_TYPE_')}</option>
          ))}
        </Select>

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onCancel} disabled={isSaving} className="rounded-md px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted disabled:opacity-50">
            {t('common.cancel')}
          </button>
          <Button type="submit" size="sm" isLoading={isSaving}>{t('fiscalization.receipts.issue')}</Button>
        </div>
      </form>
    </div>
  );
}

export default NewReceiptModal;
